// Shared building blocks: buttons, cards, empty/loading/error states, toasts, sheets, form fields.
import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from './icons.js';
import { rpc } from '../api.js';

export function Button({ variant = 'navy', size, icon, iconRight, busy, children, class: cls, ...rest }) {
  const classes = ['btn', `btn-${variant}`, size ? `btn-${size}` : '', busy ? 'is-busy' : '', cls || ''].filter(Boolean).join(' ');
  const Tag = rest.href ? 'a' : 'button';
  return (
    <Tag class={classes} type={Tag === 'button' ? rest.type || 'button' : undefined} disabled={Tag === 'button' ? rest.disabled || busy : undefined} aria-busy={busy ? 'true' : undefined} {...rest}>
      {busy ? <span class="spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children ? <span>{children}</span> : null}
      {iconRight && !busy ? <Icon name={iconRight} /> : null}
    </Tag>
  );
}

export function Chip({ tone = 'plain', children, title }) {
  return <span class={`chip chip-${tone}`} title={title}>{children}</span>;
}

export function Progress({ value, max, label }) {
  const pct = max ? Math.round((Math.min(value, max) / max) * 100) : 0;
  return (
    <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax={max || 0} aria-valuenow={value || 0} aria-label={label}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Empty({ icon = 'book', title, children, action }) {
  return (
    <div class="empty">
      <span class="empty-icon"><Icon name={icon} /></span>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
      {action || null}
    </div>
  );
}

export function Loading({ rows = 3, label = 'Loading' }) {
  return (
    <div class="loading" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => <div class="skeleton" key={i} />)}
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  return (
    <div class="notice notice-bad" role="alert">
      <Icon name="alert" />
      <div>
        <p>{(error && error.message) || 'Something went wrong.'}</p>
        {onRetry ? <Button variant="line" size="sm" icon="refresh" onClick={onRetry}>Try again</Button> : null}
      </div>
    </div>
  );
}

export function Notice({ tone = 'info', icon = 'info', children }) {
  return (
    <div class={`notice notice-${tone}`}>
      <Icon name={icon} />
      <p>{children}</p>
    </div>
  );
}

// Data from a database function, with loading/error state and a reload().
export function useRpc(name, args, deps = [], { skip = false } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: !skip });
  const seq = useRef(0);
  const load = useCallback(async (quiet) => {
    if (skip) return;
    const n = ++seq.current;
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await rpc(name, args);
      if (n === seq.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (n === seq.current) setState((s) => ({ ...s, error, loading: false }));
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load, setData: (data) => setState((s) => ({ ...s, data })) };
}

/* ---------- Toasts ---------- */
const ToastContext = createContext(() => {});
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // { replace: true } clears the others first, so a "not done" message never sits beside "done".
  const show = useCallback((message, tone = 'ok', opts) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...(opts && opts.replace ? [] : t), { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'bad' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div class="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div class={`toast toast-${t.tone}`} key={t.id}>
            <Icon name={t.tone === 'bad' ? 'alert' : 'done'} />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);

/* ---------- Sheet (dialog) ---------- */
export function Sheet({ title, open, onClose, children, footer }) {
  const ref = useRef(null);
  const opener = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    opener.current = document.activeElement;
    document.body.classList.add('sheet-open');
    const el = ref.current;
    const focusable = () => el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const first = focusable()[0];
    (el.querySelector('[autofocus]') || first || el).focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Tab') {
        const list = focusable();
        if (!list.length) return;
        const a = list[0];
        const z = list[list.length - 1];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('sheet-open');
      if (opener.current && opener.current.focus) opener.current.focus();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div class="sheet-wrap">
      <div class="sheet-backdrop" onClick={onClose} />
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-title" ref={ref} tabIndex={-1}>
        <div class="sheet-head">
          <h2 id="sheet-title">{title}</h2>
          <button class="icon-btn" type="button" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </div>
        <div class="sheet-body">{children}</div>
        {footer ? <div class="sheet-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ---------- Form fields ---------- */
let fieldSeq = 0;
export function Field({ label, hint, error, children, id }) {
  const [fid] = useState(() => id || `f${++fieldSeq}`);
  const child = typeof children === 'function' ? children(fid) : children;
  return (
    <div class={`field${error ? ' has-error' : ''}`}>
      <label for={fid}>{label}</label>
      {child}
      {hint && !error ? <p class="field-hint">{hint}</p> : null}
      {error ? <p class="field-error" role="alert">{error}</p> : null}
    </div>
  );
}

export function Section({ title, action, children, id }) {
  return (
    <section class="section" aria-labelledby={id}>
      {title ? (
        <div class="section-head">
          <h2 id={id}>{title}</h2>
          {action || null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
